import { Box, Popover, Tooltip, Typography } from '@mui/material';
import React, { MouseEvent, ReactElement, useEffect, useRef, useState } from 'react';

type EllipsisTextProps = {
    text: string | ReactElement;
    sx?: object;
    breakAll?: boolean;
    onClick?: (event: MouseEvent<HTMLElement>) => void;
    clampLines?: number;
    title?: string;
    'data-testid'?: string;
};

export default function EllipsisText({
    text,
    sx = undefined,
    breakAll = false,
    onClick = undefined,
    clampLines = 1,
    title,
    'data-testid': dataTestId
}: EllipsisTextProps) {
    const textRef = useRef<HTMLElement | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
        const element = textRef.current;
        if (!element) return;

        const checkTruncation = () => {
            setIsTruncated(element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth);
        };

        checkTruncation();
        const observer = new ResizeObserver(checkTruncation);
        observer.observe(element);
        return () => observer.disconnect();
    }, [text, clampLines]);

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        if (isTruncated) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = (e) => {
        e.stopPropagation();
        setAnchorEl(null);
    };

    return (
        <>
            <Tooltip title={title || ''} disableHoverListener={!title || !isTruncated}>
                <Box
                    ref={textRef}
                    data-testid={dataTestId}
                    className={isTruncated || Boolean(onClick) ? 'clickable clamp' : 'clamp'}
                    onClick={onClick ?? (isTruncated ? handleClick : undefined)}
                    sx={{
                        wordBreak: `break-${breakAll ? 'all' : 'word'}`,
                        display: '-webkit-box',
                        '--clamp-lines': clampLines,
                        ...sx
                    }}
                >
                    {text}
                </Box>
            </Tooltip>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
                slotProps={{
                    paper: {
                        sx: {
                            width: 'auto'
                        }
                    }
                }}
            >
                <Typography sx={{ maxWidth: '400px', p: 2, wordBreak: 'break-word' }}>{text}</Typography>
            </Popover>
        </>
    );
}
